import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FormModal } from "@/components/form-modal";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { eventTypes } from "@shared/schema";
import { useEffect } from "react";

const formSchema = z.object({
  name: z.string().min(1, "Event name is required"),
  type: z.enum(eventTypes),
  date: z.string().min(1, "Date and time are required"),
  venue: z.string().optional(),
  description: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface CreateEventModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: number;
  prePopulatedData?: {
    name?: string;
    type?: "jam" | "open_mic" | "openmic" | "generic";
    date?: string;
    venue?: string;
    description?: string;
    externalEventId?: number;
  };
  onSuccess?: (eventId: number) => void;
}

export default function CreateEventModal({ isOpen, onOpenChange, organisationId, prePopulatedData, onSuccess }: CreateEventModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: prePopulatedData?.name || "",
      type: (prePopulatedData?.type === "open_mic" ? "openmic" : prePopulatedData?.type) || "jam",
      date: prePopulatedData?.date || "",
      venue: prePopulatedData?.venue || "",
      description: prePopulatedData?.description || "",
    },
  });

  // Update form when prePopulatedData changes
  useEffect(() => {
    if (prePopulatedData && isOpen) {
      form.reset({
        name: prePopulatedData.name || "",
        type: (prePopulatedData.type === "open_mic" ? "openmic" : prePopulatedData.type) || "jam",
        date: prePopulatedData.date || "",
        venue: prePopulatedData.venue || "",
        description: prePopulatedData.description || "",
      });
    }
  }, [prePopulatedData, isOpen]);

  const createEventMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const payload = { 
        ...data, 
        organisationId,
        externalEventId: prePopulatedData?.externalEventId 
      };
      const response = await fetch("/api/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error('Failed to create event');
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast({
        title: "Success",
        description: prePopulatedData?.externalEventId 
          ? "Event converted successfully! Performers will be created from bookings."
          : "Event created successfully!",
      });
      form.reset();
      onOpenChange(false);
      if (onSuccess) {
        onSuccess(data.id);
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create event",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormData) => {
    createEventMutation.mutate(data);
  };

  const selectedEventType = form.watch("type");

  return (
    <FormModal
      open={isOpen}
      onOpenChange={onOpenChange}
      title="Create New Event"
      form={form}
      onSubmit={onSubmit}
      isLoading={createEventMutation.isPending}
      submitLabel={createEventMutation.isPending ? "Creating..." : "Create Event"}
      width="md"
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Event Name</FormLabel>
            <FormControl>
              <Input
                placeholder="Enter event name"
                {...field}
                autoComplete="off"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Event Type</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select event type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="jam">Jam Night</SelectItem>
                <SelectItem value="openmic">Open Mic</SelectItem>
                <SelectItem value="generic">Generic Event</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="date"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Date & Time</FormLabel>
            <FormControl>
              <Input
                type="datetime-local"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="venue"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Venue (Optional)</FormLabel>
            <FormControl>
              <Input placeholder="Enter venue" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Description (Optional)</FormLabel>
            <FormControl>
              <Textarea
                placeholder={
                  selectedEventType === "jam" 
                    ? "Describe your jam session..." 
                    : selectedEventType === "openmic"
                    ? "Describe your open mic event..."
                    : "Describe your event..."
                }
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </FormModal>
  );
}